import Icon from 'components/Icon';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ProgressBar } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import fileService from 'services/file.service';
import { str_shuffle } from 'utils';
import { dateDBtoBR } from 'utils/date';
import { useCancelToken } from 'utils/http-common';
import { makeid } from 'utils/random';
import BodyInputFormat from './BodyInputFormat';
import "./style/_inputfile.scss";
import { addData, format_size_byte, getData, get_byte } from './utils';
import IconFile from 'components/Icon/IconFile';

interface IPropsConfig{
  max?: number;
  prefix?: "B"|"KB"|"MB"|"GB"|"TB",
}

export interface IFormFile {
  id: number;
  url: string;
  name: string;
  size: number;
  id_internal?: string;
  created_at?: string;
  extension?: string;
}

interface IPropsFormInputFiles{
  title?: string;
  config?: IPropsConfig;
  disabled?: boolean;
  onChange?: (files: number[] | string[]) => void;
  files?: number[] | string[];
  onlyOne?: boolean;
  state?:{
    model:any,
    setModel:Function
  };
  name?: string;
}

export interface IFormFileToUpload{
  extension: string;
  id?: number;
  url:string;
  error?:boolean;
  file?: File;
  name: string;
  size: number;
  loaded: boolean;
  loading: boolean;
  id_internal: string;
  created_at?: string;
}

// function FilesEquals(f1:any, f2:any):boolean{
//   if(f1.path !== f2.path) return false;
//   if(f1.lastModified !== f2.lastModified) return false;
//   if(f1.name !== f2.name) return false;
//   if(f1.size !== f2.size) return false;
//   if(f1.type !== f2.type) return false;
//   if(f1.webkitRelativePath !== f2.webkitRelativePath) return false;

//   return true;
// }

function RowFile({
  file,
  onLoadSuccess,
  onError,
  onRemove,
  config,
  disabled
}:{
  file:IFormFileToUpload,
  onLoadSuccess: (file: IFormFile) => void,
  onError: (file: IFormFile) => void,
  onRemove: (id: string) => void,
  config:IPropsConfig,
  disabled?: boolean
}){
  const [ _file ] = useState(file.file);
  const ref = useRef<any>();
  const [ _fileInfo, set_fileInfo ] = useState(file);
  const [ error, setError ] = useState(false);
  const [ errorMessage, setErrorMessage ] = useState("");
  const { token, cancel } = useCancelToken();
  const [ _config ] = useState({
    max:config.max?config.max:-1,
    prefix:config.prefix?config.prefix:"B",
  });

  useEffect(()=>{
    function upload(){
      let max = (_config.max!==-1)?get_byte(_config.max,_config.prefix):-1;
      if(_file && !_fileInfo.id){
        if(max === -1 || _file.size <= max){
          const form = new FormData();
          form.append("file",_file);
  
          fileService.store(form,{
            onUploadProgress: (event)=>{
              let progress: number = Math.round(
                (event.loaded * 100) / event.total
              );
              if(ref && ref.current){
                let c = ref.current;
                let b = c.getElementsByClassName("progress-bar")[0];
                if(b){
                  b.style.width = `${progress}%`;
                  b.innerText = `${progress}%`;
                }
              }
            },
            cancelToken:token
          }).then((data)=>{
            set_fileInfo(old=>{
              let d = {
                ...old, loaded: true, error: false,
                id: data.id,
                url: data.url_file,
                extension: data.extension,
                size: data.size,
                created_by: data.created_by
              };
              return d ;
            });
            cancel()
          }).catch((e)=>{
            if(e.cancel){
              setErrorMessage("Upload cancelado")
            }
            if(e.response){
              if(e.response.status === 413){
                setErrorMessage("Upload muito grande")
              }
            }
            setError(true);
            
          });
        } else {
          setErrorMessage(`Tamanho maximo ${format_size_byte(max)}`);
          setError(true);
        }
      }
    }
    upload();
  },[_file, token, _config, ref, _fileInfo, cancel]);

  useEffect(()=>{
    if(_fileInfo.loaded && _fileInfo.id){
      let aux:IFormFile = {
        url: _fileInfo.url,
        name: _fileInfo.name,
        size: _fileInfo.size,
        id_internal: _fileInfo.id_internal,
        id: _fileInfo.id,
      };
      onLoadSuccess(aux);
    }
  },[_fileInfo, onLoadSuccess])
  
  return (
    <>
      <tr className={error?"error-file":""} style={{width: "100%"}}>
        <td style={{width: "46px"}} rowSpan={2}>
          <IconFile extencion={_fileInfo.extension}/>
        </td>
        <td className="name-area">
          {_fileInfo.url?<a href={_fileInfo.url} rel="noreferrer" target="_blank">{_fileInfo.name}</a>:_fileInfo.name}
          {/* {_fileInfo.name} */}
        </td>
        <td style={{width:"90px", whiteSpace:"nowrap"}}>{format_size_byte(_fileInfo.size)}</td>
        {disabled?<></>:<td className='btn-area'>
          {_fileInfo.loaded?
            <Icon name="trash" onClick={()=>{
              onRemove(_fileInfo.id_internal);
            }}/>
          :
            <Icon name="times" onClick={()=>{
              if(error){
                onRemove(_fileInfo.id_internal);
              } else {
                cancel()
              }
            }}/>
          }
        </td>}
      </tr>
      <tr>
        <td className='load-bar-area' colSpan={disabled?2:3}>
          {
            (()=>{
              if(errorMessage){
                return errorMessage;
              }
              if(!_file && _fileInfo.created_at){
                return _fileInfo.created_at
              }
              return <ProgressBar animated={!_fileInfo.loaded} ref={ref} variant={error?"danger":"primary"}/>;
            })()
          }
        </td>
      </tr>
    </>
  );
}

function create_id(name: string, size: number){
  return `${makeid(5)}-${str_shuffle(`${name}${size}`)}-${makeid(7)}`
}

export default function FormInputFiles({
  title, 
  config={
    max: -1,
    prefix: "B"
  },
  disabled,
  onChange,
  files,
  state,
  name,
  ...props
}:IPropsFormInputFiles){
  const [ _files_id ] = useState<number[] | string[]>(()=>{
    if(state && state.model && name){
      let d = getData(state.model,name);
      if(d && Array.isArray(d)){
        return d;
      }
    }
    if(!files) return [];
    return files;
  });
  const [ _files, _setFiles ] = useState<IFormFile[]>([]);
  const [ filesToUpload, setFilesToUpload ] = useState<IFormFileToUpload[]>(()=>{
    let list:any[] = [..._files];
    return list.map(f=>{
      return {
        id: f.id,
        url: f.url,
        name: f.name,
        size: f.size,
        loaded: true,
        loading: false,
        id_internal: f.id_internal,
        created_at: f.created_at,
        extension: f.extension,
      }
    });
  });

  useEffect(()=>{
    if(_files_id.length){
      fileService.get({params:{"id":_files_id.join(",")}}).then((data)=>{
        _setFiles(data.map((f:any)=>{
          let created_at = dateDBtoBR(f.created_at);
          return {
            id: f.id,
            url: f.url_file,
            name: f.name,
            size: f.size,
            created_at: created_at,
            extension: f.extension,
          };
        }))
        setFilesToUpload(data.map((f:any)=>{
          let created_at = dateDBtoBR(f.created_at);
          return {
            id: f.id,
            url: f.url_file,
            name: f.name,
            size: f.size,
            loaded: true,
            loading: false,
            id_internal: f.id_internal,
            created_at: created_at,
            extension: f.extension,
          };
        }))
      });
    }
  },[_files_id]);

  const stateSetModel = useCallback((callback:(old:any)=>any)=>{
    if(state && state.setModel && name){
      state.setModel(callback);
    }
  },[state, name]);

  useEffect(()=>{
    let id_only = _files.map(f=>f.id);
    if(onChange)onChange(id_only);
    if(name){
      stateSetModel((old:any)=>{
        return addData(old,name,id_only);
      });
    }
  },[_files, stateSetModel, name, onChange]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFilesToUpload((old)=>{
      let aux = [...old];
      acceptedFiles.forEach((file) => {
        aux.push({
          file:file,
          name:file.name,
          size:file.size,
          loaded:false,
          loading:false,
          id_internal: create_id(file.name,file.size),
          url:"",
          extension: "",
        });
      });
      return aux;
    });
  },[]);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject
  } = useDropzone({ 
    onDrop, 
    disabled, 
    // maxSize: config.max!==-1?config.max:undefined
  });

  return (
    <BodyInputFormat props={{title,...props}}>
      {disabled?<></>:<div
        {...getRootProps({
          className:
            "dropzone" +
            (isDragActive ? ' active' : '') +
            (isDragAccept ? ' accept' : '') +
            (isDragReject ? ' reject' : '')
          ,
          onClick:(e: { currentTarget: any; })=>{
            let target = e.currentTarget;
            if(target){
              target.classList.add("active");
            }
          },
          onBlur:(e: { currentTarget: any; })=>{
            let target = e.currentTarget;
            if(target){
              target.classList.remove("active");
            }
          }
        })}
      >
        <input {...getInputProps()} disabled={false} />
        {!isDragActive ? (
          <span>{'Arraste aqui seu arquivo ou clique'}</span>
        ) : isDragAccept ? (
          <span>{'Largar agora'}</span>
        ) : (
          <span>{'Arquivo não aceito'}</span>
        )}
      </div>}
      <table className='table-input-file'>
        <tbody>
          {filesToUpload.length?filesToUpload.map((file, key)=>{
            return (
              <RowFile disabled={disabled} file={file} key={key} config={config} 
              onLoadSuccess={(file:IFormFile)=>{
                _setFiles((old)=>{
                  if(old.filter(f=>f.id_internal === file.id_internal).length>0){
                    return old;
                  }

                  return [...old, file];
                })
              }}
              onError={(file:IFormFile)=>{

              }}
              onRemove={(id:string)=>{
                setFilesToUpload((old)=>{
                  return old.filter(f=>f.id_internal !== id);
                });
                _setFiles((old)=>{
                  return old.filter(f=>f.id_internal !== id);
                });
              }}/>
            );
          }):(
            <tr>
              <td>Sem arquivo</td>
            </tr>
          )}
        </tbody>
      </table>
    </BodyInputFormat>
  );
}